import React, { memo, useRef, type ComponentType } from 'react';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { IssueTableSkeleton } from '../common/ui/skeleton/index.tsx';
import type { Props } from './types.tsx';
import { nativeIssueTableEntrypoint } from './entrypoint.tsx';

const AsyncNativeIssueTable = lazyForPaint<ComponentType<Props>>(
	() => import(/* webpackChunkName: "async-issue-navigator-native-issue-table" */ './index'),
);

const AsyncNativeIssueTableOld: ComponentType<Props> = memo<Props>((props: Props) => (
	<Placeholder fallback={<IssueTableSkeleton />} name="async-native-issue-table">
		<AsyncNativeIssueTable {...props} />
	</Placeholder>
));

const entryPointParams = {};

const useNativeIssueTableEntryPoint = () => {
	const hasLoaded = useRef(false);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		nativeIssueTableEntrypoint,
		entryPointParams,
	);

	if (!hasLoaded.current) {
		// nativeIssueTableEntrypoint is nested in Project Issue Navigator's EntryPoint and is preloaded as router resource
		// this is to make sure we still load this EntryPoint outside PIN (e.g. GIN)
		entryPointActions.load();
		// https://hello.atlassian.net/wiki/spaces/UAF/pages/2333138515/Tutorial+How+to+render+your+EntryPoint#Option-3%3A-Using-useRef-without-user-interaction
		hasLoaded.current = true;
	}

	return { entryPointReferenceSubject };
};

const AsyncNativeIssueTableNew = memo<Props>((props) => {
	const { entryPointReferenceSubject } = useNativeIssueTableEntryPoint();

	return (
		<JiraEntryPointContainer
			id="native-issue-table-entrypoint"
			packageName="jiraNativeIssueTable"
			placeholderName="native-issue-table"
			entryPointReferenceSubject={entryPointReferenceSubject}
			runtimeProps={props}
			fallback={<IssueTableSkeleton />}
		/>
	);
});

const AsyncNativeIssueTableConditional = componentWithFG(
	'jira_list_issue_table_entrypoint',
	AsyncNativeIssueTableNew,
	AsyncNativeIssueTableOld,
);

export default AsyncNativeIssueTableConditional;
